import type { FC, ReactElement } from 'react';
import { useMemo } from 'react';

import { L10nText } from '../../../../../services/uiTranslations/reactBindings/L10nText';
import type { Message } from '../../../../../store/slices/chat/types';
import { SendType } from '../../../../../store/slices/chat/types';
import { useModifiers } from '../../../../../styles/hooks/useModifiers';
import { parseSystemMessage } from '../../../../../utils/chat';
import { Icon } from '../../../../icons/icon';
import { Icons } from '../../../../icons/iconNames';
import { MessageContentConnected } from '../messageContent/MessageContentConnected';

import styles from './MessageBubble.module.css';

interface ExtraPropsType {
  currentUserId: string;
  isMiniChat?: boolean;
}

interface MessageProps
  extends Pick<Message, 'content' | 'role' | 'userId' | 'userScreenName' | 'sendType' | 'requireTranslation'> {}

interface MessageBubbleProps extends MessageProps, ExtraPropsType {}

export const MessageBubble: FC<MessageBubbleProps> = ({
  content,
  role,
  userId,
  userScreenName,
  currentUserId,
  sendType,
  requireTranslation,
  isMiniChat,
}) => {
  const isCurrentUserMessage = userId === currentUserId;

  const isLiveSupportMessage =
    ['LIVE_SUPPORT', 'SUPPORT'].includes(role) ||
    ['FRESHCHAT_AGENT', 'FRESHCHAT_BOT'].includes(userScreenName as string);

  const isSystemMessage = role === 'SUPPORT' && sendType === SendType.Table;
  const isAutoReplyMessage = role === 'SUPPORT' && userScreenName === 'Auto-reply';
  const isNotificationMsg = sendType === SendType.Notification;

  const mods = useModifiers(
    styles,
    'messageBubble',
    {
      accent: isLiveSupportMessage || isSystemMessage || isAutoReplyMessage,
      border: isCurrentUserMessage,
      dark: isCurrentUserMessage || isLiveSupportMessage || isSystemMessage || isAutoReplyMessage || isNotificationMsg,
      left: !isCurrentUserMessage,
      miniChat: isMiniChat,
      right: isCurrentUserMessage,
    },
    { withBaseClass: true }
  );

  const notificationMods = useModifiers(styles, 'messageBubble', {
    notification: isNotificationMsg,
  });

  // Render username label based on message type
  const renderUserName = useMemo(
    (
      currentUser = isCurrentUserMessage,
      liveSupport = isLiveSupportMessage || isSystemMessage || isAutoReplyMessage
    ): ReactElement | null => {
      if (currentUser) {
        return (
          <>
            <L10nText tk="CHAT_YOU" />:{' '}
          </>
        );
      }

      if (liveSupport) {
        return (
          <>
            <span className={styles.messageBubble__liveSupportIcon}>
              <Icon icon={Icons.Support} />
            </span>
            <L10nText tk="LIVE_SUPPORT" />:{' '}
          </>
        );
      }

      return userScreenName ? <>{userScreenName}: </> : null;
    },
    [isAutoReplyMessage, isCurrentUserMessage, isLiveSupportMessage, isSystemMessage, userScreenName]
  );

  const renderMessageContent = useMemo((): ReactElement => {
    if (isSystemMessage) {
      if (isMiniChat) {
        return <span>{parseSystemMessage(content)}</span>; // Return one-liner without HTML tags
      }

      return <div dangerouslySetInnerHTML={{ __html: content }} />; // Preserve HTML tags and layout
    }

    return (
      <span className={notificationMods}>
        <MessageContentConnected content={content} requireTranslation={requireTranslation} />
      </span>
    );
  }, [content, notificationMods, isMiniChat, isSystemMessage, requireTranslation]);

  return (
    <div className={mods}>
      <div className={styles.messageBubble__content}>
        <span className={styles.messageBubble__userName}>{renderUserName}</span>
        {renderMessageContent}
      </div>
    </div>
  );
};
